import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { setupDemoData } from "@/scripts/setupDemo";
import { Loader2, Users, Building2, Briefcase, Users2, FileCheck, TrendingUp, AlertCircle } from "lucide-react";

export default function SetupDemo() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const navigate = useNavigate();

  const handleSetup = async () => {
    setLoading(true);
    setResult(null);
    
    const response = await setupDemoData();
    setResult(response);
    setLoading(false);
  };

  const dataSummary = result?.data?.data_summary;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl shadow-lg">
        <CardHeader className="space-y-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl">Setup Demo Environment</CardTitle>
            <div className="flex gap-2">
              <Badge variant="default" className="bg-blue-600">Enhanced v2.0</Badge>
              {result?.success && <Badge variant="default" className="bg-green-600">Ready</Badge>}
            </div>
          </div>
          <CardDescription>
            Create comprehensive demo data across all portal types for testing AthleteNexus
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* What will be created */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Institution Portal</h3>
              </div>
              <ul className="text-sm space-y-1 text-muted-foreground pl-7">
                <li>7 Demo users with roles</li>
                <li>5 Student-athletes (5 sports)</li>
                <li>20 NIL deals with AI analysis</li>
                <li>2 Institutional sponsors</li>
                <li>4 Revenue sharing pools</li>
                <li>5 Eligibility records</li>
                <li>5 Financial aid tracking</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Brand Portal</h3>
              </div>
              <ul className="text-sm space-y-1 text-muted-foreground pl-7">
                <li>3 Brands (Nike, TechGear, Local Fitness)</li>
                <li>3 Brand portal users</li>
                <li>Linked to athlete deals</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Consulting Firm Portal</h3>
              </div>
              <ul className="text-sm space-y-1 text-muted-foreground pl-7">
                <li>2 Consulting firms</li>
                <li>2 Firm portal users</li>
                <li>Institution access granted</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users2 className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Collective & Agency Portals</h3>
              </div>
              <ul className="text-sm space-y-1 text-muted-foreground pl-7">
                <li>1 NIL Collective + 1 Agency</li>
                <li>3 Organization users</li>
                <li>Athlete relationships</li>
                <li>2 Active campaigns</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FileCheck className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Compliance & Messaging</h3>
              </div>
              <ul className="text-sm space-y-1 text-muted-foreground pl-7">
                <li>2 Title IX compliance records</li>
                <li>4 Internal messages</li>
                <li>4 Audit log entries (hash-chain)</li>
                <li>2 Associated entities (flagged)</li>
                <li>5 Time tracking records</li>
                <li>3 Recruitment records</li>
              </ul>
            </div>
          </div>

          <Separator />

          {/* Demo Highlights */}
          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Demo Highlights:</h4>
            <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <li>🚫 <strong>3 Prohibited Deals</strong>: Alcohol, Gambling, Cannabis (AI flagged critical)</li>
              <li>⚠️ <strong>2 Conflict Deals</strong>: Adidas, Powerade (institutional sponsor conflicts)</li>
              <li>✅ <strong>8 Approved Deals</strong>: Low to medium risk, fully compliant</li>
              <li>⏳ <strong>5 Pending Deals</strong>: Awaiting compliance review</li>
              <li>📝 <strong>2 Draft Deals</strong>: Athlete-created, incomplete</li>
              <li>📊 <strong>AI Risk Distribution</strong>: 6 low, 5 medium, 3 high, 3 critical</li>
            </ul>
          </div>

          <Separator />

          {/* Setup button */}
          <div className="space-y-4">
            <Button 
              onClick={handleSetup} 
              disabled={loading}
              className="w-full"
              size="lg"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {loading ? "Setting up demo environment..." : "Create Complete Demo Environment"}
            </Button>

            <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>All demo accounts use password: <code className="bg-background px-1.5 py-0.5 rounded font-mono">Demo2025!</code></p>
            </div>
          </div>

          {/* Result message */}
          {result && (
            <div className={`p-4 rounded-lg border-2 ${result.success ? 'bg-green-50 border-green-200 text-green-900' : 'bg-red-50 border-red-200 text-red-900'}`}>
              <div className="flex items-center gap-2 font-semibold mb-2">
                {result.success ? <FileCheck className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
                {result.success ? 'Demo environment created successfully!' : 'Setup failed'}
              </div>
              {result.error && (
                <p className="text-sm mt-1">{result.error.toString()}</p>
              )}
              {dataSummary && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div className="bg-white/50 p-2 rounded">
                    <div className="font-semibold">{dataSummary.athletes}</div>
                    <div className="text-xs">Athletes</div>
                  </div>
                  <div className="bg-white/50 p-2 rounded">
                    <div className="font-semibold">{dataSummary.deals}</div>
                    <div className="text-xs">Deals</div>
                  </div>
                  <div className="bg-white/50 p-2 rounded">
                    <div className="font-semibold">{dataSummary.brands}</div>
                    <div className="text-xs">Brands</div>
                  </div>
                  <div className="bg-white/50 p-2 rounded">
                    <div className="font-semibold">{dataSummary.revenue_pools}</div>
                    <div className="text-xs">Revenue Pools</div>
                  </div>
                  <div className="bg-white/50 p-2 rounded">
                    <div className="font-semibold">{dataSummary.compensation_records}</div>
                    <div className="text-xs">Compensation</div>
                  </div>
                  <div className="bg-white/50 p-2 rounded">
                    <div className="font-semibold">{dataSummary.title_ix_records}</div>
                    <div className="text-xs">Title IX</div>
                  </div>
                  <div className="bg-white/50 p-2 rounded">
                    <div className="font-semibold">{dataSummary.messages}</div>
                    <div className="text-xs">Messages</div>
                  </div>
                  <div className="bg-white/50 p-2 rounded">
                    <div className="font-semibold">{dataSummary.audit_log_entries}</div>
                    <div className="text-xs">Audit Logs</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Login credentials and links */}
          {result?.success && (
            <div className="space-y-4">
              <Separator />
              
              <div className="space-y-3">
                <h3 className="font-semibold text-foreground">Institution Portal Users:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="bg-muted/50 p-2 rounded">
                    <p className="font-medium">Super Admin</p>
                    <p className="text-muted-foreground text-xs">admin@athletenexus-demo.com</p>
                  </div>
                  <div className="bg-muted/50 p-2 rounded">
                    <p className="font-medium">Compliance Officer</p>
                    <p className="text-muted-foreground text-xs">compliance@athletenexus-demo.com</p>
                  </div>
                  <div className="bg-muted/50 p-2 rounded">
                    <p className="font-medium">Athletic Director</p>
                    <p className="text-muted-foreground text-xs">athletic.director@athletenexus-demo.com</p>
                  </div>
                  <div className="bg-muted/50 p-2 rounded">
                    <p className="font-medium">Coach</p>
                    <p className="text-muted-foreground text-xs">coach@athletenexus-demo.com</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-foreground">Athlete Portal Users:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="bg-muted/50 p-2 rounded">
                    <p className="font-medium">Marcus Johnson (Football)</p>
                    <p className="text-muted-foreground text-xs">athlete.marcus@athletenexus-demo.com</p>
                  </div>
                  <div className="bg-muted/50 p-2 rounded">
                    <p className="font-medium">Sarah Williams (Basketball)</p>
                    <p className="text-muted-foreground text-xs">athlete.sarah@athletenexus-demo.com</p>
                  </div>
                </div>
              </div>

              <Separator />
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Quick Access
                  </h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Button 
                    onClick={() => navigate("/auth")}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                  >
                    Institution Login
                  </Button>
                  <Button 
                    onClick={() => navigate("/dashboard")}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                  >
                    Dashboard
                  </Button>
                  <Button 
                    onClick={() => navigate("/dashboard/athletes")}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                  >
                    Athletes
                  </Button>
                  <Button 
                    onClick={() => navigate("/dashboard/deals")}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                  >
                    Deals
                  </Button>
                  <Button 
                    onClick={() => navigate("/dashboard/caps")}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                  >
                    Revenue Pools
                  </Button>
                  <Button 
                    onClick={() => navigate("/dashboard/compensation")}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                  >
                    Compensation
                  </Button>
                  <Button 
                    onClick={() => navigate("/dashboard/title-ix")}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                  >
                    Title IX
                  </Button>
                  <Button 
                    onClick={() => navigate("/dashboard/messages")}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                  >
                    Messages
                  </Button>
                  <Button 
                    onClick={() => navigate("/dashboard/audit")}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                  >
                    Audit Log
                  </Button>
                  <Button 
                    onClick={() => navigate("/dashboard/analytics")}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                  >
                    Analytics
                  </Button>
                  <Button 
                    onClick={() => navigate("/athlete/portal")}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                  >
                    Athlete Portal
                  </Button>
                  <Button 
                    onClick={() => navigate("/brand/portal")}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                  >
                    Brand Portal
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
